import { Avatar, Box, Flex, Icon } from "@chakra-ui/react";
import { GenericAsyncMultiSelect } from "components/Form/Select/GenericAsyncMultiSelect";
import { SelectFieldProps } from "components/Form/typings";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { UserOrChannelResponse } from "models/v2/roleUserChannel";
import { FiHash, FiLock } from "react-icons/fi";
import { Text14 } from "components/Text";
import CaminoUser from "components/Icon/CaminoUser";
import { chakraComponents, MultiValueProps, GroupBase } from "chakra-react-select";
import RecipientChipFromOption from "components/Tag/ResourceTargetTag/RecipientChipFromOption";
import { fromCompositeId, toCompositeId } from "helpers/compositeId";
import apiClient from "services/ApiClient";
import { createQueryString } from "helpers/queryString";
import { OptionType } from "components/Form/Select";
import { useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { startCase } from "lodash";
import PlusCirlceButton from "components/Button/PlusCircleButton";
import FormField from "../FormField";

function EmptyClearIndicator() {
  return null;
}

function PlusButtonControl({
  controlProps,
  menuIsOpen,
  hasChannel,
  onOpenMenu,
}: {
  controlProps: any;
  menuIsOpen: boolean;
  hasChannel: boolean;
  onOpenMenu: () => void;
}) {
  const { children, innerRef, innerProps } = controlProps;
  if (menuIsOpen) {
    return <chakraComponents.Control {...controlProps} />;
  }
  return (
    <chakraComponents.Control {...controlProps}>
      <Flex ref={innerRef} {...innerProps} align="start" justify="flex-start" gap={2} flexWrap="wrap" pl={2} pr={2}>
        {children}
        {!hasChannel && (
          <PlusCirlceButton
            mt="2px"
            label="Add recipient"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onOpenMenu();
            }}
          />
        )}
      </Flex>
    </chakraComponents.Control>
  );
}

function roleLabel(name: string) {
  return startCase(name);
}

function showName(
  data: { rawValue: UserOrChannelResponse },
  meta?: { context?: "menu" | "value" },
) {
  if (!data?.rawValue) {
    return "";
  }
  // Show the user's email only in the menu (dropdown); the selected chip stays clean.
  const showEmail = meta?.context === "menu";
  if (data?.rawValue?.recordType === "MessageServiceChannel") {
    const icon = data.rawValue.channelType === "private" ? FiLock : FiHash;
    return (
      <Flex align="center" gap="1">
        <Icon boxSize="3" as={icon} />
        <Text14>{data?.rawValue?.name}</Text14>
      </Flex>
    );
  } else if (data?.rawValue?.recordType === "User") {
    return (
      <Flex align="center" gap="2">
        <Avatar
          boxSize="18px"
          src={data.rawValue.avatarUrl ?? undefined}
          icon={<CaminoUser boxSize={2.5} />}
          bg="#B4B4B4"
          color="white"
        />
        <Text14>{data?.rawValue?.name}</Text14>
        {showEmail && data.rawValue.workEmail && (
          <Text14 color="fg.subtle">— {data.rawValue.workEmail}</Text14>
        )}
      </Flex>
    );
  } else if (data?.rawValue?.recordType === "OnboardingJourneyNetworkRelation") {
    return (
      <Box>
        <Text14>{roleLabel(data?.rawValue?.name || "")}</Text14>
      </Box>
    );
  }
  return "";
}

const RECIPIENT_CHIP_TYPES = ["User", "OnboardingJourneyNetworkRelation", "CustomEmail"];

const embedOption = (userOrChannel: UserOrChannelResponse) => ({
  value: toCompositeId(userOrChannel.recordType, userOrChannel.id),
  label:
    userOrChannel.recordType === "OnboardingJourneyNetworkRelation"
      ? roleLabel(userOrChannel?.name || "")
      : userOrChannel?.name || "",
  rawValue: userOrChannel,
});

const extractValue = (userOrChannel: UserOrChannelResponse) =>
  toCompositeId(userOrChannel.recordType, userOrChannel.id);

function makeInlineLabelControl(inlineLabel: string) {
  return function InlineLabelControl({ children, ...rest }: any) {
    return (
      <chakraComponents.Control {...rest}>
        <Box
          position="absolute"
          left="0.75rem"
          top="50%"
          transform="translateY(-50%)"
          color="content.tertiary"
          fontSize="sm"
          zIndex={1}
          pointerEvents="none"
        >
          {inlineLabel}
        </Box>
        {children}
      </chakraComponents.Control>
    );
  };
}

async function getInitialValue(value: string) {
  const { resourceId, resourceType } = fromCompositeId(value);
  const queryString = resourceId ? `?${createQueryString({ additionalQueries: { resourceId, resourceType } })}` : "";
  const results = await apiClient.get(`${API_ROUTES.rolesUsersChannels}${queryString}`);
  return (results || [])
    .map((userOrChannel: UserOrChannelResponse) => ({
      value: toCompositeId(userOrChannel.recordType, userOrChannel.id),
      label:
        userOrChannel.recordType === "OnboardingJourneyNetworkRelation"
          ? roleLabel(userOrChannel?.name || "")
          : userOrChannel?.name || "",
      rawValue: userOrChannel,
    }))
    .find((option: OptionType) => option.value === value);
}

export type SelectableType = "users" | "channels" | "roles";

interface UserOrChannelSelectFieldProps extends SelectFieldProps<UserOrChannelResponse> {
  types?: SelectableType[];
  maxSelections?: number;
  inlineLabel?: string;
  channelExclusive?: boolean;
  showPlusButton?: boolean;
}

export default function UserOrChannelSelectField({
  name,
  label,
  helperText,
  errors,
  required,
  variant,
  placeholder = "#a-channel, @somebody, or somebody@example.com ...",
  formFieldProps = {},
  containerStyles = {},
  onChangeCallback,
  types = ["users", "channels", "roles"],
  maxSelections = 1,
  inlineLabel,
  channelExclusive = false,
  showPlusButton = false,
  components: passedComponents,
  ...props
}: UserOrChannelSelectFieldProps & { components?: Record<string, React.ComponentType<any>> }) {
  const formVariant = variant;

  // When channelExclusive is true:
  // - selecting a channel locks the field (no more selections allowed)
  // - selecting users/roles hides channels from the options list
  const rawValues = useWatch({ name, control: props.control, defaultValue: [] });
  const currentValues: string[] = Array.isArray(rawValues) ? rawValues : [];
  const hasChannel = channelExclusive && currentValues.some((v: string) => v?.startsWith?.("MessageServiceChannel:"));
  const hasNonChannel =
    channelExclusive && currentValues.some((v: string) => v && !v.startsWith?.("MessageServiceChannel:"));
  const effectiveTypes = useMemo(() => {
    if (!channelExclusive) return types;
    if (hasNonChannel) return types.filter((t) => t !== "channels");
    return types;
  }, [channelExclusive, hasNonChannel, types]);

  const fetchPath = `${API_ROUTES.rolesUsersChannels}?${createQueryString({ additionalQueries: { types: effectiveTypes } })}`;

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const mergedComponents = useMemo(() => {
    const base: Record<string, any> = { ...(passedComponents || {}) };
    // Render selected user/role chips via the shared adapter so they match
    // assignee / recipient tags elsewhere. Unknown record types (e.g. channels)
    // fall back to chakra-react-select's default chip.
    base.MultiValue = function MultiValueChip(p: MultiValueProps<any, true, GroupBase<any>>) {
      const raw = p.data?.rawValue;
      if (!raw || !RECIPIENT_CHIP_TYPES.includes(raw.recordType)) {
        return <chakraComponents.MultiValue {...p} />;
      }
      return (
        <Box mr={1}>
          <RecipientChipFromOption
            recipient={{
              recordType: raw.recordType,
              label: raw.name,
              name: raw.name,
              avatarUrl: raw.avatarUrl,
            }}
            onRemove={p.removeProps.onClick as (() => void) | undefined}
          />
        </Box>
      );
    };
    if (showPlusButton) {
      // Note: showPlusButton overrides inlineLabel's Control if both are set
      if (!hasChannel) {
        base.ClearIndicator = EmptyClearIndicator;
      }
      base.DropdownIndicator = null;
      base.Control = function PlusButtonControlWrapper(cp: any) {
        return (
          <PlusButtonControl
            controlProps={cp}
            menuIsOpen={menuIsOpen}
            hasChannel={hasChannel}
            onOpenMenu={() => setMenuIsOpen(true)}
          />
        );
      };
    } else if (inlineLabel) {
      base.Control = makeInlineLabelControl(inlineLabel);
    }
    return Object.keys(base).length > 0 ? base : undefined;
  }, [inlineLabel, passedComponents, showPlusButton, hasChannel, menuIsOpen]);

  const inlineLabelSx = inlineLabel
    ? {
        "& .chakra-react-select__value-container": { pl: "2rem !important" },
        "& .chakra-react-select__placeholder": { mt: "-2px" },
      }
    : undefined;

  return (
    <FormField
      name={name}
      label={formVariant === "floating" ? label : undefined}
      errors={errors}
      helperText={helperText}
      required={required}
      variant={formVariant}
      {...formFieldProps}
    >
      <Box sx={inlineLabelSx}>
        <GenericAsyncMultiSelect<UserOrChannelResponse>
          cacheKey={fetchPath}
          fetchPath={fetchPath}
          resolveInitialValue={getInitialValue}
          embedOption={embedOption}
          extractValue={extractValue}
          name={name}
          variant={formVariant}
          formatOptionLabel={showName}
          placeholder={formVariant === "floating" ? " " : placeholder}
          containerStyles={{ width: "100%", cursor: "text", ...containerStyles }}
          onChangeCallback={onChangeCallback}
          maxSelections={maxSelections}
          readOnly={hasChannel}
          isClearable={hasChannel}
          perPagePagination={300}
          filterOption={() => true}
          {...props}
          components={mergedComponents}
          {...(showPlusButton
            ? {
                menuIsOpen,
                openMenuOnClick: false,
                onMenuOpen: () => setMenuIsOpen(true),
                onMenuClose: () => setMenuIsOpen(false),
              }
            : {})}
        />
      </Box>
    </FormField>
  );
}
